/* eslint-disable camelcase */
import { NotAllowedIcon } from '@chakra-ui/icons'
import {
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  useDisclosure,
  Input,
  Checkbox,
  Select as ChakraSelect,
} from '@chakra-ui/react'
import { SingleDatepicker } from 'chakra-dayzed-datepicker'
import { MultiSelect, Option } from 'chakra-multiselect'
import { addMinutes, format, isBefore, parse } from 'date-fns'
import { useEffect, useState } from 'react'
import Api from '../api/api'

/**
 * Reservation blocks: a date (range), optionally a time window, a location
 * and specific tables, during which no table reservation is taken (holiday,
 * private event, maintenance). Creates new blocks; shows + deletes an
 * existing one when `viewingBlock` is set.
 */
export const RESTAURANT_TABLES = [101, 102, 103, 104, 105, 106, 107, 108, 109, 110, 'CINEMA']
export const TERRACE_TABLES = [201, 202, 203, 204, 205, 206, 207, 208]

export const describeBlock = (block: any): string => {
  const parts: string[] = []
  parts.push(block.reason ? block.reason : 'Blocked')
  if (block.time_from || block.time_to) {
    parts.push(`${block.time_from ?? '…'}–${block.time_to ?? '…'}`)
  } else {
    parts.push('all day')
  }
  if (block.location) {
    parts.push(block.location === 'terace' ? 'terrace' : block.location)
  }
  if (block.tables && block.tables.length > 0) {
    parts.push(block.tables.join(', '))
  }
  return parts.join(' · ')
}

const BlockModal = ({ date, blockChanged, viewingBlock, stopViewingBlock }: any) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [loading, setLoading] = useState(false)

  const defaultBlock = {
    id: null,
    date_from: new Date(),
    date_to: new Date(),
    all_day: true,
    time_from: '8:00',
    time_to: '14:00',
    location: '',
    tables: [] as string[],
    reason: '',
    user: null as any,
  }
  const [block, setBlock] = useState<any>(defaultBlock)

  useEffect(() => {
    if (viewingBlock && !isOpen) {
      setBlock({
        id: viewingBlock.id,
        date_from: new Date(viewingBlock.date_from),
        date_to: new Date(viewingBlock.date_to),
        all_day: !viewingBlock.time_from && !viewingBlock.time_to,
        time_from: viewingBlock.time_from ?? '',
        time_to: viewingBlock.time_to ?? '',
        location: viewingBlock.location ?? '',
        tables: viewingBlock.tables ?? [],
        reason: viewingBlock.reason ?? '',
        user: viewingBlock.user ?? null,
      })
      onOpen()
    }
  }, [viewingBlock])

  const openNew = () => {
    const base = date ? new Date(date) : new Date()
    setBlock({ ...defaultBlock, date_from: base, date_to: base })
    onOpen()
  }

  const update = (key: string, value: any) => {
    if (key === 'tables') {
      value = value.map((t: Option) => (t.value ? t.value : t))
    }
    if (key === 'location' && value !== block.location) {
      setBlock({ ...block, location: value, tables: [] })
      return
    }
    if (key === 'date_from' && isBefore(block.date_to, value)) {
      setBlock({ ...block, date_from: value, date_to: value })
      return
    }
    setBlock({ ...block, [key]: value })
  }

  const hours = () => {
    const out: { key: string; value: string }[] = []
    let t = parse('08:00', 'HH:mm', new Date())
    const end = parse('23:59', 'HH:mm', new Date())
    while (!isBefore(end, t)) {
      out.push({ key: format(t, 'H:mm'), value: format(t, 'H:mm') })
      t = addMinutes(t, 30)
    }
    out.push({ key: '24:00', value: '24:00' })
    return out
  }

  const tablesFor = () => {
    if (block.location === 'restaurant') return RESTAURANT_TABLES
    if (block.location === 'terace') return TERRACE_TABLES
    return []
  }

  const close = () => {
    setBlock(defaultBlock)
    if (stopViewingBlock) stopViewingBlock()
    onClose()
  }

  const create = () => {
    if (!block.all_day && (!block.time_from || !block.time_to)) {
      alert('Please choose the time window or tick "All day"')
      return
    }
    setLoading(true)
    Api.createBlock({
      date_from: block.date_from,
      date_to: block.date_to,
      time_from: block.all_day ? null : block.time_from,
      time_to: block.all_day ? null : block.time_to,
      location: block.location || null,
      tables: block.location ? block.tables : [],
      reason: block.reason || null,
    })
      .then((result: any) => {
        if (!result.ok) {
          alert(result.message ?? 'Could not create the block')
          return
        }
        close()
        blockChanged()
      })
      .finally(() => setLoading(false))
  }

  const remove = () => {
    if (!window.confirm(`Remove this block (${block.reason || 'no reason'})?`)) {
      return
    }
    setLoading(true)
    Api.deleteBlock(block.id)
      .then(() => {
        close()
        blockChanged()
      })
      .finally(() => setLoading(false))
  }

  const readOnly = !!block.id

  return (
    <>
      <Button
        onClick={openNew}
        position='fixed'
        bottom={16}
        right={4}
        variant='solid'
        colorScheme='red'
        zIndex={9}
        title='Block a day / time (no reservations taken)'
      >
        <NotAllowedIcon />
      </Button>

      <Modal isOpen={isOpen} onClose={close} size={'xl'}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{readOnly ? 'Reservation block' : 'Block reservations'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack alignItems='flex-start' spacing={4} w='full'>
              <Text fontSize='sm' color='gray.600'>
                While a block is active no table reservation is taken for that period — not by
                staff here (without confirming), not by the phone AI agent.
              </Text>
              <HStack w='full'>
                <Text minWidth='90px'>From:</Text>
                <SingleDatepicker
                  date={block.date_from}
                  onDateChange={(e) => update('date_from', e)}
                  disabled={readOnly}
                  configs={{ dateFormat: 'yyyy-MM-dd', firstDayOfWeek: 1 }}
                />
              </HStack>
              <HStack w='full'>
                <Text minWidth='90px'>To:</Text>
                <SingleDatepicker
                  date={block.date_to}
                  onDateChange={(e) => update('date_to', e)}
                  disabled={readOnly}
                  configs={{ dateFormat: 'yyyy-MM-dd', firstDayOfWeek: 1 }}
                />
              </HStack>
              <HStack w='full'>
                <Text minWidth='90px'>All day:</Text>
                <Checkbox
                  size='md'
                  colorScheme='red'
                  isChecked={block.all_day}
                  isDisabled={readOnly}
                  onChange={(e) => update('all_day', e.target.checked)}
                />
              </HStack>
              {!block.all_day && (
                <HStack w='full'>
                  <Text minWidth='90px'>Time:</Text>
                  <ChakraSelect
                    value={block.time_from}
                    variant='filled'
                    isDisabled={readOnly}
                    onChange={(e) => update('time_from', e.target.value)}
                  >
                    {hours().map((o) => (
                      <option key={`from-${o.key}`} value={o.key}>
                        {o.value}
                      </option>
                    ))}
                  </ChakraSelect>
                  <Text>to</Text>
                  <ChakraSelect
                    value={block.time_to}
                    variant='filled'
                    isDisabled={readOnly}
                    onChange={(e) => update('time_to', e.target.value)}
                  >
                    {hours().map((o) => (
                      <option key={`to-${o.key}`} value={o.key}>
                        {o.value}
                      </option>
                    ))}
                  </ChakraSelect>
                </HStack>
              )}
              <HStack w='full'>
                <Text minWidth='90px'>Location:</Text>
                <ChakraSelect
                  value={block.location}
                  variant='filled'
                  isDisabled={readOnly}
                  onChange={(e) => update('location', e.target.value)}
                >
                  <option value=''>Everything (restaurant + terrace)</option>
                  <option value='restaurant'>Restaurant</option>
                  <option value='terace'>Terrace</option>
                </ChakraSelect>
              </HStack>
              {block.location && (
                <HStack w='full'>
                  <Text minWidth='90px'>Tables:</Text>
                  <div style={{ width: '100%' }}>
                    {readOnly ? (
                      <Text>{block.tables && block.tables.length ? block.tables.join(', ') : 'all tables'}</Text>
                    ) : (
                      <MultiSelect
                        style={{ width: '100%' }}
                        onChange={(e) => update('tables', e)}
                        options={tablesFor().map((t) => ({ label: t.toString(), value: t.toString() }))}
                        value={block.tables}
                        placeholder='all tables (leave empty)'
                      />
                    )}
                  </div>
                </HStack>
              )}
              <HStack w='full'>
                <Text minWidth='90px'>Reason:</Text>
                <Input
                  value={block.reason ?? ''}
                  isDisabled={readOnly}
                  placeholder='e.g. Holiday, Private event, Maintenance'
                  onChange={(e) => update('reason', e.target.value)}
                />
              </HStack>
              {readOnly && block.user && (
                <Text fontSize='sm' color='gray.600'>
                  Added by {block.user.name}
                </Text>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter justifyContent={readOnly ? 'space-between' : 'end'}>
            {readOnly && (
              <Button colorScheme='red' ml={0} onClick={remove} isLoading={loading}>
                Remove block
              </Button>
            )}
            <HStack>
              <Button colorScheme='facebook' ml={0} onClick={close} isLoading={loading}>
                Close
              </Button>
              {!readOnly && (
                <Button colorScheme='red' mr={0} onClick={create} isLoading={loading}>
                  Block
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default BlockModal
